import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import { InputWrapper, LabelStyle } from "../../styles/form";
import {
  decodeNumberFilter,
  getBetweenError,
  NUMBER_OPERATORS,
  updateCustomHeaderValues,
  updateNumberCustomHeaderValue,
} from "./customColumnFilterUtils";

export type CustomColumnDef = {
  label: string;
  varname: string;
  questionLabel?: string;
  questionType: string;
  questionChoices?: { label: string; id: string }[];
};

type Props = {
  column: CustomColumnDef;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  showLabel?: boolean;
};

const CustomColumnFilterField = ({
  column,
  value,
  onChange,
  showLabel = true,
}: Props) => {
  const displayLabel =
    column.questionLabel || column.label || column.varname;

  if (column.questionType === "number") {
    const raw = typeof value === "string" ? value : "";
    const decoded = decodeNumberFilter(raw);
    const { op = "", val = "", min = "", max = "" } = decoded;
    const betweenError = op === "between" ? getBetweenError(min, max) : null;

    const handleNumberChange = (
      newOp: string,
      newVal: string,
      newMin: string,
      newMax: string,
    ) => {
      const result = updateNumberCustomHeaderValue(
        column.varname,
        newOp,
        newVal,
        newMin,
        newMax,
        raw ? [{ key: column.varname, value: raw }] : [],
      );
      const entry = result.find((v) => v.key === column.varname);
      onChange(entry?.value ?? "");
    };

    return (
      <FormControl sx={{ ...InputWrapper, width: "100%" }}>
        {showLabel && (
          <FormLabel sx={LabelStyle}>{column.label}</FormLabel>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Select
            value={op}
            onChange={(e) =>
              handleNumberChange(e.target.value, val, min, max)
            }
            displayEmpty
            sx={{ minWidth: 90 }}
            renderValue={
              op
                ? undefined
                : () => (
                    <Typography sx={{ color: "#c1cccf" }}>Op</Typography>
                  )
            }
          >
            {NUMBER_OPERATORS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>

          {op === "between" ? (
            <>
              <TextField
                value={min}
                onChange={(e) =>
                  handleNumberChange(op, val, e.target.value, max)
                }
                placeholder="Min"
                type="number"
                error={!!betweenError}
                sx={{ flex: 1, minWidth: 80 }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.5 }}>
                and
              </Typography>
              <TextField
                value={max}
                onChange={(e) =>
                  handleNumberChange(op, val, min, e.target.value)
                }
                placeholder="Max"
                type="number"
                error={!!betweenError}
                sx={{ flex: 1, minWidth: 80 }}
              />
            </>
          ) : (
            <TextField
              value={val}
              onChange={(e) =>
                handleNumberChange(op, e.target.value, min, max)
              }
              placeholder="Value"
              type="number"
              disabled={!op}
              sx={{ flex: 1, minWidth: 80 }}
            />
          )}
        </Box>
        {betweenError && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {betweenError}
          </Typography>
        )}
      </FormControl>
    );
  }

  if (["text", "date"].includes(column.questionType)) {
    const textVal = typeof value === "string" ? value : "";
    return (
      <FormControl sx={{ ...InputWrapper, width: "100%" }}>
        {showLabel && (
          <FormLabel sx={LabelStyle}>{displayLabel}</FormLabel>
        )}
        <TextField
          value={textVal}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          placeholder={`Enter ${displayLabel.toLowerCase()}`}
          InputProps={{
            endAdornment: textVal ? (
              <InputAdornment position="end">
                <IconButton onClick={() => onChange("")} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </FormControl>
    );
  }

  const selectedIds = Array.isArray(value) ? value : [];
  return (
    <FormControl sx={{ ...InputWrapper, width: "100%" }}>
      {showLabel && <FormLabel sx={LabelStyle}>{displayLabel}</FormLabel>}
      <Select
        multiple
        value={selectedIds}
        onChange={(e) => onChange(e.target.value as string[])}
        fullWidth
        displayEmpty
        renderValue={(ids) => {
          const idList = ids as string[];
          if (idList.length === 0) {
            return (
              <Typography sx={{ color: "#c1cccf" }}>Select</Typography>
            );
          }
          return idList
            .map(
              (id) =>
                (column.questionChoices || []).find((c) => c.id === id)
                  ?.label ?? id,
            )
            .join(", ");
        }}
        endAdornment={
          selectedIds.length > 0 ? (
            <InputAdornment sx={{ marginRight: "10px" }} position="end">
              <IconButton onClick={() => onChange([])} size="small">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      >
        {(column.questionChoices || []).map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export { updateCustomHeaderValues };
export default CustomColumnFilterField;
